import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, FileText, Plus, Check, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money, dt, d, API } from "@/api";
import { Badge, MarginTag, MODE_LABELS, STATUS_LABELS, DIRECTIONS, DIRECTION_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const TABS = [
  { k: "ozet", l: "Özet" }, { k: "ayaklar", l: "Taşıma Ayakları" }, { k: "olaylar", l: "Olaylar" },
  { k: "evrak", l: "Evraklar" }, { k: "finans", l: "Maliyet & Kârlılık" }, { k: "portal", l: "Müşteri Portalı" },
];
const PORTAL_FIELDS = [
  { k: "durum", l: "Durum" }, { k: "rota", l: "Rota" }, { k: "eta", l: "ETA" },
  { k: "tasima_modu", l: "Taşıma Modu" }, { k: "yuk_bilgisi", l: "Yük Bilgisi" },
  { k: "olaylar", l: "Olay Geçmişi" }, { k: "belgeler", l: "Belgeler" },
];

export default function OperationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [tab, setTab] = useState("ozet");
  const [data, setData] = useState(null);
  const [parties, setParties] = useState([]);
  const fileRef = useRef({});
  const [leg, setLeg] = useState({ sequence: 1, mode: "kara", origin: "", destination: "", carrier_id: "", vehicle: "", etd: "", eta: "", is_transfer: false });
  const [ev, setEv] = useState({ type: "not", description: "", delay_hours: 0, location: "" });
  const [charge, setCharge] = useState({ kind: "maliyet", category: "", amount: "", currency: "USD", fx_rate: "", is_extra: false, supplier_id: "", invoice_no: "", description: "" });
  const [chargeEdit, setChargeEdit] = useState(null);
  const [docName, setDocName] = useState("");
  const [refs, setRefs] = useState({ bl_no: "", container_no: "" });
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [portal, setPortal] = useState({ enabled: false, fields: [], document_ids: [] });
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/operations/${id}`);
      setData(res);
      setLoadError("");
      setPortal({
        enabled: !!res.operation.portal_enabled,
        fields: res.operation.portal_fields || [],
        document_ids: res.operation.portal_document_ids || [],
      });
      setLeg((l) => ({ ...l, sequence: (res.legs?.length || 0) + 1 }));
      setRefs({ bl_no: res.operation.bl_no || "", container_no: res.operation.container_no || "" });
    } catch (e) {
      setLoadError(errMsg(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/parties").then((r) => setParties(r.data)).catch(() => {});
  }, [load]);

  if (loadError)
    return (
      <div className="space-y-4" data-testid="operation-detail-error">
        <button onClick={() => nav("/operasyonlar")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-950" data-testid="back-button">
          <ArrowLeft className="h-4 w-4" /> Operasyonlar
        </button>
        <div className="brut p-6">
          <p className="overline">Operasyon açılamadı</p>
          <p className="mt-2 text-sm text-red-700">{loadError}</p>
        </div>
      </div>
    );
  if (!data) return <div className="flex h-64 items-center justify-center" data-testid="operation-detail-loading"><Loader2 className="h-6 w-6 animate-spin text-klein" /></div>;
  const op = data.operation, fin = data.finance;
  const suppliers = parties.filter((p) => p.type === "tedarikci");

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };

  const changeStatus = (s) => act(() => api.post(`/operations/${id}/status`, { status: s }), `Durum güncellendi: ${STATUS_LABELS[s]}`);

  const upload = async (docId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documents/${docId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Belge yüklendi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openFile = async (fileId, name) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="operation-detail-page">
      <button onClick={() => nav("/operasyonlar")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-950" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Operasyonlar
      </button>

      <div className="brut p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="overline">Pozisyon Dosyası</p>
            <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="operation-no">{op.no}</h1>
            <p className="mt-1 text-sm text-zinc-600">{op.customer_name} · {op.origin} → {op.destination} · {MODE_LABELS[op.mode]}</p>
            <p className="mt-1 font-mono text-xs text-zinc-500">
              BL: <span data-testid="operation-blno">{op.bl_no || "—"}</span> · Konteyner/Araç: <span data-testid="operation-containerno">{op.container_no || "—"}</span>
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <Badge value={op.status} testId="operation-status-badge" />
            {can("operation:write") && data.allowed_transitions.length > 0 && (
              <div className="flex flex-wrap justify-end gap-2" data-testid="status-actions">
                {data.allowed_transitions.map((s) => (
                  <button key={s} className="brut-btn-ghost !px-3 !py-1.5 !text-xs" onClick={() => changeStatus(s)}
                    data-testid={`status-to-${s}`}>{STATUS_LABELS[s]}</button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 border-t border-zinc-200 pt-4 text-sm sm:grid-cols-4">
          <div><p className="overline">Planlanan Satış</p><p className="tnum font-mono">{money(fin.planned_sale_try)}</p></div>
          <div><p className="overline">Geçerli Maliyet</p><p className="tnum font-mono">{money(fin.effective_cost_try)}</p></div>
          <div><p className="overline">Kâr</p><p className="tnum font-mono" data-testid="operation-profit">{money(fin.profit_try)}</p></div>
          <div><p className="overline">Marj</p><p data-testid="operation-margin"><MarginTag pct={fin.margin_pct} /></p></div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-zinc-200" data-testid="operation-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`border-b-2 px-4 py-2 text-sm transition-colors ${tab === t.k ? "border-zinc-950 font-bold text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "ozet" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="tab-content-ozet">
          <div className="brut p-4 sm:p-6">
            <p className="overline">Yük ve Sevkiyat</p>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">Yük</dt><dd>{op.cargo_desc}</dd></div>
              <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">Ağırlık</dt><dd className="tnum font-mono">{op.weight_kg} kg</dd></div>
              <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">İşlem Tipi</dt><dd data-testid="detail-direction">{DIRECTION_LABELS[op.direction] || "Export"}</dd></div>
              <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">Taşıma Birimi</dt><dd className="font-mono" data-testid="detail-equipment">{op.equipment || "-"}</dd></div>
              {op.mode === "deniz" && (
                <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">TEU</dt><dd className="tnum font-mono" data-testid="detail-teu">{op.teu || 0}</dd></div>
              )}
              {op.mode === "kara" && (
                <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">Araç Adedi</dt><dd className="tnum font-mono" data-testid="detail-vehicle-count">{op.vehicle_count || 0}</dd></div>
              )}
              <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">ETA</dt><dd>{d(op.eta)}</dd></div>
              <div className="flex justify-between border-b border-zinc-100 pb-1"><dt className="text-zinc-500">Toplam Gecikme</dt><dd className="tnum font-mono">{op.total_delay_hours || 0} saat</dd></div>
            </dl>
            <div className="mt-5 border-t border-zinc-200 pt-4">
              <div className="flex items-center justify-between">
                <p className="overline">Dosya Bilgileri</p>
                {can("operation:write") && (
                  <button className="brut-btn-ghost !px-3 !py-1 !text-xs" data-testid="edit-operation-button"
                    onClick={() => setEdit(edit ? null : {
                      no: op.no || "", direction: op.direction || "export", customer_id: op.customer_id, mode: op.mode, equipment: op.equipment || "20DC",
                      origin: op.origin, destination: op.destination, cargo_desc: op.cargo_desc,
                      weight_kg: op.weight_kg || 0, teu: op.teu || 0, vehicle_count: op.vehicle_count || 0,
                      eta: (op.eta || "").slice(0, 10),
                      bl_no: op.bl_no || "", container_no: op.container_no || "",
                      planned_cost_try: op.planned_cost_try || 0, planned_sale_try: op.planned_sale_try || 0,
                      description: op.description || "", notes: op.notes || "",
                    })}>
                    <Pencil className="h-3 w-3" /> {edit ? "Kapat" : "Düzenle"}
                  </button>
                )}
              </div>
              {edit && (
                <form className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="operation-edit-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    act(() => api.put(`/operations/${id}`, {
                      ...edit, weight_kg: Number(edit.weight_kg || 0), eta: edit.eta || null,
                      teu: edit.mode === "deniz" ? Number(edit.teu || 0) : 0,
                      vehicle_count: edit.mode === "kara" ? Number(edit.vehicle_count || 0) : 0,
                      planned_cost_try: Number(edit.planned_cost_try || 0),
                      planned_sale_try: Number(edit.planned_sale_try || 0),
                    }), "Operasyon bilgileri güncellendi").then((ok) => { if (ok) setEdit(null); });
                  }}>
                  <label className="sm:col-span-2 block">
                    <span className="block text-xs font-mono uppercase text-zinc-500">Poz No</span>
                    <input required placeholder="SG-2026-0001" className="brut-input mt-1 w-full" value={edit.no}
                      onChange={(e) => setEdit({ ...edit, no: e.target.value })} data-testid="edit-no-input" />
                  </label>
                  <input required placeholder="Çıkış" className="brut-input" value={edit.origin}
                    onChange={(e) => setEdit({ ...edit, origin: e.target.value })} data-testid="edit-origin-input" />
                  <input required placeholder="Varış" className="brut-input" value={edit.destination}
                    onChange={(e) => setEdit({ ...edit, destination: e.target.value })} data-testid="edit-destination-input" />
                  <select className="brut-input" value={edit.direction} onChange={(e) => setEdit({ ...edit, direction: e.target.value })} data-testid="edit-direction-select">
                    {DIRECTIONS.map((x) => <option key={x} value={x}>{DIRECTION_LABELS[x]}</option>)}
                  </select>
                  <select className="brut-input" value={edit.mode} onChange={(e) => setEdit({ ...edit, mode: e.target.value })} data-testid="edit-mode-select">
                    <option value="kara">Kara</option><option value="deniz">Deniz</option><option value="hava">Hava</option>
                  </select>
                  <select className="brut-input" value={edit.equipment} onChange={(e) => setEdit({ ...edit, equipment: e.target.value })} data-testid="edit-equipment-select">
                    {["20DC", "40HQ", "45HCPW", "40OT", "40FR", "Parsiyel", "Full", "Havayolu"].map((x) => <option key={x}>{x}</option>)}
                  </select>
                  <input required placeholder="Yük açıklaması" className="brut-input" value={edit.cargo_desc}
                    onChange={(e) => setEdit({ ...edit, cargo_desc: e.target.value })} data-testid="edit-cargo-input" />
                  <input type="number" step="0.01" placeholder="Ağırlık (kg)" className="brut-input" value={edit.weight_kg}
                    onChange={(e) => setEdit({ ...edit, weight_kg: e.target.value })} data-testid="edit-weight-input" />
                  {edit.mode === "deniz" && (
                    <input type="number" step="0.1" placeholder="TEU" className="brut-input" value={edit.teu}
                      onChange={(e) => setEdit({ ...edit, teu: e.target.value })} data-testid="edit-teu-input" />
                  )}
                  {edit.mode === "kara" && (
                    <input type="number" step="1" placeholder="Araç adedi" className="brut-input" value={edit.vehicle_count}
                      onChange={(e) => setEdit({ ...edit, vehicle_count: e.target.value })} data-testid="edit-vehiclecount-input" />
                  )}
                  <input type="date" className="brut-input" value={edit.eta}
                    onChange={(e) => setEdit({ ...edit, eta: e.target.value })} data-testid="edit-eta-input" />
                  <input type="number" step="0.01" placeholder="Planlanan maliyet (TRY)" className="brut-input" value={edit.planned_cost_try}
                    onChange={(e) => setEdit({ ...edit, planned_cost_try: e.target.value })} data-testid="edit-cost-input" />
                  <input type="number" step="0.01" placeholder="Planlanan satış (TRY)" className="brut-input" value={edit.planned_sale_try}
                    onChange={(e) => setEdit({ ...edit, planned_sale_try: e.target.value })} data-testid="edit-sale-input" />
                  <textarea rows={3} placeholder="Açıklama" className="brut-input sm:col-span-2" value={edit.description}
                    onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="edit-description-input" />
                  <textarea rows={3} placeholder="Not (iç ekip notu)" className="brut-input sm:col-span-2" value={edit.notes}
                    onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="edit-notes-input" />
                  <button className="brut-btn sm:col-span-2" data-testid="operation-edit-save">Değişiklikleri Kaydet</button>
                </form>
              )}
            </div>

            <div className="mt-5 border-t border-zinc-200 pt-4">
              <p className="overline">Açıklama & Not</p>
              <div className="mt-2 space-y-3 text-sm">
                <div>
                  <p className="text-xs uppercase text-zinc-500">Açıklama</p>
                  <p className="whitespace-pre-line" data-testid="detail-description">{op.description || "-"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-zinc-500">Not</p>
                  <p className="whitespace-pre-line" data-testid="detail-notes">{op.notes || "-"}</p>
                </div>
              </div>
            </div>

            <div className="mt-5 border-t border-zinc-200 pt-4">
              <p className="overline">Konşimento & Konteyner / Araç</p>
              <form className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="refs-form"
                onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/operations/${id}/refs`, refs), "Konşimento ve konteyner bilgisi kaydedildi"); }}>
                <input placeholder="Konşimento (BL) No" className="brut-input" value={refs.bl_no}
                  onChange={(e) => setRefs({ ...refs, bl_no: e.target.value })} data-testid="refs-blno-input" />
                <input placeholder="Konteyner / Araç No" className="brut-input" value={refs.container_no}
                  onChange={(e) => setRefs({ ...refs, container_no: e.target.value })} data-testid="refs-containerno-input" />
                {can("operation:write") && <button className="brut-btn" data-testid="refs-save-button">Kaydet</button>}
              </form>
            </div>

            <div className="mt-5">
              <p className="overline">Kısmi / Birleştirme / Bölme Kayıtları</p>
              <ul className="mt-2 space-y-1 text-sm">
                {data.shipments.map((s) => (
                  <li key={s.id} className="border-b border-zinc-100 py-1">{s.kind} · {s.description} · <span className="tnum font-mono">{s.weight_kg} kg</span></li>
                ))}
                {data.shipments.length === 0 && <li className="text-zinc-500">Kayıt yok.</li>}
              </ul>
              {can("operation:write") && (
                <ShipmentForm onSave={(body) => act(() => api.post(`/operations/${id}/shipments`, body), "Sevkiyat kaydı eklendi")} />
              )}
            </div>
          </div>
          <div className="brut p-4 sm:p-6">
            <p className="overline">Durum Makinesi</p>
            <ol className="mt-4 border-l-2 border-zinc-200 pl-4">
              {["taslak", "planlandi", "yolda", "aktarma", "varista", "teslim_edildi", "kapandi"].map((s) => {
                const active = s === op.status;
                const done = ["taslak", "planlandi", "yolda", "aktarma", "varista", "teslim_edildi", "kapandi"].indexOf(s) <
                  ["taslak", "planlandi", "yolda", "aktarma", "varista", "teslim_edildi", "kapandi"].indexOf(op.status);
                return (
                  <li key={s} className="relative mb-4">
                    <span className={`absolute -left-[22px] top-1 h-3 w-3 rounded-full border-2 ${active ? "border-klein bg-klein" : done ? "border-green-600 bg-green-600" : "border-zinc-300 bg-white"}`} />
                    <p className={`text-sm ${active ? "font-bold text-zinc-950" : done ? "text-green-700" : "text-zinc-400"}`}>{STATUS_LABELS[s]}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}

      {tab === "ayaklar" && (
        <div className="brut" data-testid="tab-content-ayaklar">
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead><tr><th>#</th><th>Mod</th><th>Rota</th><th>Taşıyıcı</th><th>Araç</th><th>ETD</th><th>ETA</th><th>Aktarma</th></tr></thead>
              <tbody>
                {data.legs.map((l, i) => (
                  <tr key={l.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`leg-row-${l.id}`}>
                    <td className="tnum">{l.sequence}</td><td>{MODE_LABELS[l.mode]}</td>
                    <td className="text-xs">{l.origin} → {l.destination}</td>
                    <td>{l.carrier_name}</td><td>{l.vehicle || "-"}</td>
                    <td className="text-xs">{d(l.etd)}</td><td className="text-xs">{d(l.eta)}</td>
                    <td>{l.is_transfer ? "Evet" : "-"}</td>
                  </tr>
                ))}
                {data.legs.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-zinc-500">Taşıma ayağı tanımlı değil.</td></tr>}
              </tbody>
            </table>
          </div>
          {can("operation:write") && (
            <form className="grid grid-cols-1 gap-3 border-t border-zinc-200 p-4 sm:grid-cols-4" data-testid="leg-form"
              onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/operations/${id}/legs`, { ...leg, sequence: Number(leg.sequence), carrier_id: leg.carrier_id || null, etd: leg.etd || null, eta: leg.eta || null }), "Taşıma ayağı eklendi"); }}>
              <input type="number" className="brut-input" value={leg.sequence} onChange={(e) => setLeg({ ...leg, sequence: e.target.value })} data-testid="leg-sequence-input" />
              <select className="brut-input" value={leg.mode} onChange={(e) => setLeg({ ...leg, mode: e.target.value })} data-testid="leg-mode-select">
                <option value="kara">Kara</option><option value="deniz">Deniz</option><option value="hava">Hava</option>
              </select>
              <input required placeholder="Çıkış" className="brut-input" value={leg.origin} onChange={(e) => setLeg({ ...leg, origin: e.target.value })} data-testid="leg-origin-input" />
              <input required placeholder="Varış" className="brut-input" value={leg.destination} onChange={(e) => setLeg({ ...leg, destination: e.target.value })} data-testid="leg-destination-input" />
              <select className="brut-input" value={leg.carrier_id} onChange={(e) => setLeg({ ...leg, carrier_id: e.target.value })} data-testid="leg-carrier-select">
                <option value="">Taşıyıcı / Tedarikçi</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input placeholder="Araç / Sefer" className="brut-input" value={leg.vehicle} onChange={(e) => setLeg({ ...leg, vehicle: e.target.value })} data-testid="leg-vehicle-input" />
              <input type="date" className="brut-input" value={leg.etd} onChange={(e) => setLeg({ ...leg, etd: e.target.value })} data-testid="leg-etd-input" />
              <input type="date" className="brut-input" value={leg.eta} onChange={(e) => setLeg({ ...leg, eta: e.target.value })} data-testid="leg-eta-input" />
              <label className="flex items-center gap-2 text-xs text-zinc-600">
                <input type="checkbox" checked={leg.is_transfer} onChange={(e) => setLeg({ ...leg, is_transfer: e.target.checked })} data-testid="leg-transfer-checkbox" />
                Aktarma ayağı
              </label>
              <button className="brut-btn sm:col-span-3" data-testid="leg-save-button"><Plus className="h-4 w-4" /> Ayak Ekle</button>
            </form>
          )}
        </div>
      )}

      {tab === "olaylar" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="tab-content-olaylar">
          <div className="brut p-4 sm:p-6 lg:col-span-2">
            <p className="overline">Zaman Çizelgesi</p>
            <ol className="mt-4 border-l-2 border-zinc-200 pl-4">
              {data.events.map((e) => (
                <li key={e.id} className="relative mb-5" data-testid={`event-item-${e.id}`}>
                  <span className={`absolute -left-[22px] top-1.5 h-3 w-3 rounded-full border-2 ${e.type === "gecikme" ? "border-red-500 bg-red-500" : e.type === "teslim" ? "border-green-600 bg-green-600" : "border-klein bg-white"}`} />
                  <p className="text-sm font-semibold text-zinc-950">{e.description}</p>
                  <p className="text-xs text-zinc-500">
                    {e.type}{e.location ? ` · ${e.location}` : ""}{e.delay_hours ? ` · ${e.delay_hours} saat gecikme` : ""} · {dt(e.happened_at)}
                  </p>
                </li>
              ))}
              {data.events.length === 0 && <li className="text-sm text-zinc-500">Olay kaydı yok.</li>}
            </ol>
          </div>
          {can("operation:write") && (
            <form className="brut space-y-3 p-4 sm:p-6" data-testid="event-form"
              onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/operations/${id}/events`, { ...ev, delay_hours: Number(ev.delay_hours) }), "Olay kaydedildi").then(() => setEv({ type: "not", description: "", delay_hours: 0, location: "" })); }}>
              <p className="overline">Yeni Olay</p>
              <select className="brut-input" value={ev.type} onChange={(e) => setEv({ ...ev, type: e.target.value })} data-testid="event-type-select">
                {["yukleme", "cikis", "aktarma", "gecikme", "varis", "teslim", "not"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input required placeholder="Açıklama" className="brut-input" value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} data-testid="event-description-input" />
              <input placeholder="Lokasyon" className="brut-input" value={ev.location} onChange={(e) => setEv({ ...ev, location: e.target.value })} data-testid="event-location-input" />
              <input type="number" step="0.5" placeholder="Gecikme (saat)" className="brut-input" value={ev.delay_hours} onChange={(e) => setEv({ ...ev, delay_hours: e.target.value })} data-testid="event-delay-input" />
              <button className="brut-btn w-full" data-testid="event-save-button">Olayı Kaydet</button>
            </form>
          )}
        </div>
      )}

      {tab === "evrak" && (
        <div className="brut" data-testid="tab-content-evrak">
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead><tr><th>Belge</th><th>Durum</th><th>Son Tarih</th><th>Dosya</th><th></th></tr></thead>
              <tbody>
                {data.documents.map((dc, i) => {
                  const overdue = dc.status !== "tamam" && dc.due_date && dc.due_date < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={dc.id} className={overdue ? "bg-red-50" : i % 2 ? "bg-zinc-50" : ""} data-testid={`document-row-${dc.id}`}>
                      <td className="font-semibold">{dc.name}</td>
                      <td><Badge value={dc.status} /></td>
                      <td className={`text-xs ${overdue ? "font-semibold text-red-600" : ""}`}>{d(dc.due_date)}</td>
                      <td className="text-xs">
                        {dc.file_id ? (
                          <button className="inline-flex items-center gap-1 text-klein hover:underline" onClick={() => openFile(dc.file_id, dc.file_name)} data-testid={`view-file-${dc.id}`}>
                            <Eye className="h-3 w-3" /> {dc.file_name}
                          </button>
                        ) : "-"}
                      </td>
                      <td className="text-right">
                        {can("document:write") && (
                          <>
                            <input type="file" className="hidden" ref={(el) => (fileRef.current[dc.id] = el)}
                              onChange={(e) => upload(dc.id, e.target.files[0])} data-testid={`file-input-${dc.id}`} />
                            <button className="brut-btn-ghost !px-2 !py-1 !text-xs" onClick={() => fileRef.current[dc.id]?.click()} data-testid={`upload-button-${dc.id}`}>
                              <Upload className="h-3 w-3" /> Yükle
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {can("document:write") && (
            <form className="flex flex-wrap gap-3 border-t border-zinc-200 p-4" data-testid="document-form"
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData();
                fd.append("name", docName);
                act(() => api.post(`/operations/${id}/documents`, fd), "Belge kalemi eklendi").then(() => setDocName(""));
              }}>
              <input required placeholder="Belge adı" className="brut-input max-w-xs" value={docName} onChange={(e) => setDocName(e.target.value)} data-testid="document-name-input" />
              <button className="brut-btn" data-testid="document-save-button"><FileText className="h-4 w-4" /> Kontrol Listesine Ekle</button>
            </form>
          )}
        </div>
      )}

      {tab === "finans" && (
        <div className="space-y-4" data-testid="tab-content-finans">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[['planned-cost', "Planlanan Maliyet", fin.planned_cost_try], ['actual-cost', "Gerçekleşen Maliyet", fin.actual_cost_try],
              ['approved-extra-cost', "Onaylı Ek Masraf", fin.extra_cost_try], ['effective-cost', "Geçerli Maliyet", fin.effective_cost_try],
              ['effective-sale', "Geçerli Satış", fin.effective_sale_try], ['pending', "Onay Bekleyen", fin.pending_try]].map(([k, l, v]) => (
              <div key={k} className="brut p-4" data-testid={`finance-metric-${k}`}><p className="overline">{l}</p><p className="tnum mt-2 font-mono text-lg font-semibold">{money(v)}</p></div>
            ))}
          </div>
          <div className="brut overflow-x-auto">
            <table className="dense w-full">
              <thead><tr><th>Tip</th><th>Kalem</th><th>Tedarikçi</th><th>Fatura No</th><th className="!text-right">Tutar</th><th>Kur</th><th className="!text-right">TRY</th><th>Durum</th><th></th></tr></thead>
              <tbody>
                {fin.charges.map((c, i) => (
                  <tr key={c.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`charge-row-${c.id}`}>
                    <td>{c.kind === "maliyet" ? "Maliyet" : "Satış"}{c.is_extra ? " (Ek)" : ""}</td>
                    <td>{c.category}</td><td>{c.supplier_name}</td>
                    <td className="font-mono text-xs" data-testid={`charge-invoice-${c.id}`}>{c.invoice_no || "-"}</td>
                    <td className="tnum text-right font-mono">{money(c.amount, c.currency)}</td>
                    <td className="font-mono text-xs">{c.fx_rate}{c.fx_source === "manuel" ? " (manuel)" : ""}</td>
                    <td className="tnum text-right font-mono">{money(c.amount_try)}</td>
                    <td><Badge value={c.status} /></td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        {c.status === "bekliyor" && can("charge:approve") && (
                          <button className="brut-btn !px-2 !py-1 !text-xs" onClick={() => act(() => api.post(`/charges/${c.id}/approve`), "Ek masraf onaylandı")} data-testid={`approve-charge-${c.id}`}>
                            <Check className="h-3 w-3" /> Onayla
                          </button>
                        )}
                        {can("charge:write") && (
                          <>
                            <button className="brut-btn-ghost !px-2 !py-1 !text-xs" data-testid={`edit-charge-${c.id}`}
                              onClick={() => setChargeEdit(chargeEdit?.id === c.id ? null : {
                                id: c.id, kind: c.kind, category: c.category, amount: c.amount,
                                currency: c.currency, fx_rate: c.currency === "TRY" ? "" : c.fx_rate,
                                is_extra: !!c.is_extra, supplier_id: c.supplier_id || "",
                                invoice_no: c.invoice_no || "", description: c.description || "",
                              })}>
                              <Pencil className="h-3 w-3" /> Düzenle
                            </button>
                            <button className="brut-btn-ghost !px-2 !py-1 !text-xs !text-red-600" data-testid={`delete-charge-${c.id}`}
                              onClick={() => { if (window.confirm("Bu kalem kalıcı olarak silinecek. Onaylıyor musunuz?")) act(() => api.delete(`/charges/${c.id}`), "Kalem silindi"); }}>
                              <Trash2 className="h-3 w-3" /> Sil
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {fin.charges.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-zinc-500">Masraf/gelir kalemi yok.</td></tr>}
              </tbody>
            </table>
            {chargeEdit && can("charge:write") && (
              <form className="grid grid-cols-1 gap-3 border-t-2 border-zinc-900 bg-amber-50 p-4 sm:grid-cols-6" data-testid="charge-edit-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const { id: cid, ...body } = chargeEdit;
                  act(() => api.put(`/charges/${cid}`, {
                    ...body, amount: Number(body.amount),
                    fx_rate: body.currency === "TRY" || !body.fx_rate ? null : Number(body.fx_rate),
                    supplier_id: body.supplier_id || null,
                  }), "Kalem güncellendi").then((ok) => { if (ok !== false) setChargeEdit(null); });
                }}>
                <p className="overline sm:col-span-6">Kalemi Düzenle</p>
                <select className="brut-input" value={chargeEdit.kind} onChange={(e) => setChargeEdit({ ...chargeEdit, kind: e.target.value })} data-testid="charge-edit-kind-select">
                  <option value="maliyet">Maliyet</option><option value="satis">Satış</option>
                </select>
                <input required placeholder="Kalem" className="brut-input" value={chargeEdit.category} onChange={(e) => setChargeEdit({ ...chargeEdit, category: e.target.value })} data-testid="charge-edit-category-input" />
                <input required type="number" step="0.01" placeholder="Tutar" className="brut-input" value={chargeEdit.amount} onChange={(e) => setChargeEdit({ ...chargeEdit, amount: e.target.value })} data-testid="charge-edit-amount-input" />
                <select className="brut-input" value={chargeEdit.currency} onChange={(e) => setChargeEdit({ ...chargeEdit, currency: e.target.value })} data-testid="charge-edit-currency-select">
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <input type="number" step="0.0001" placeholder="Kur (manuel)" className="brut-input"
                  disabled={chargeEdit.currency === "TRY"} value={chargeEdit.currency === "TRY" ? "" : chargeEdit.fx_rate}
                  onChange={(e) => setChargeEdit({ ...chargeEdit, fx_rate: e.target.value })} data-testid="charge-edit-fxrate-input" />
                <select className="brut-input" value={chargeEdit.supplier_id} onChange={(e) => setChargeEdit({ ...chargeEdit, supplier_id: e.target.value })} data-testid="charge-edit-supplier-select">
                  <option value="">Tedarikçi (ops.)</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input placeholder="Fatura No" className="brut-input sm:col-span-2" value={chargeEdit.invoice_no}
                  onChange={(e) => setChargeEdit({ ...chargeEdit, invoice_no: e.target.value })} data-testid="charge-edit-invoiceno-input" />
                <label className="flex items-center gap-2 text-xs text-zinc-600 sm:col-span-2">
                  <input type="checkbox" checked={chargeEdit.is_extra} onChange={(e) => setChargeEdit({ ...chargeEdit, is_extra: e.target.checked })} data-testid="charge-edit-extra-checkbox" />
                  Ek masraf (onay gerekir)
                </label>
                <button className="brut-btn" data-testid="charge-edit-save-button">Kaydet</button>
                <button type="button" className="brut-btn-ghost" onClick={() => setChargeEdit(null)} data-testid="charge-edit-cancel-button">Vazgeç</button>
              </form>
            )}
            {can("charge:write") && (
              <form className="grid grid-cols-1 gap-3 border-t border-zinc-200 p-4 sm:grid-cols-6" data-testid="charge-form"
                onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/operations/${id}/charges`, { ...charge, amount: Number(charge.amount), fx_rate: charge.currency === "TRY" || !charge.fx_rate ? null : Number(charge.fx_rate), supplier_id: charge.supplier_id || null }), "Kalem eklendi").then(() => setCharge({ kind: "maliyet", category: "", amount: "", currency: charge.currency, fx_rate: "", is_extra: false, supplier_id: "", invoice_no: "", description: "" })); }}>
                <select className="brut-input" value={charge.kind} onChange={(e) => setCharge({ ...charge, kind: e.target.value })} data-testid="charge-kind-select">
                  <option value="maliyet">Maliyet</option><option value="satis">Satış</option>
                </select>
                <input required placeholder="Kalem (navlun, demuraj...)" className="brut-input" value={charge.category} onChange={(e) => setCharge({ ...charge, category: e.target.value })} data-testid="charge-category-input" />
                <input required type="number" step="0.01" placeholder="Tutar" className="brut-input" value={charge.amount} onChange={(e) => setCharge({ ...charge, amount: e.target.value })} data-testid="charge-amount-input" />
                <select className="brut-input" value={charge.currency} onChange={(e) => setCharge({ ...charge, currency: e.target.value })} data-testid="charge-currency-select">
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <input type="number" step="0.0001" placeholder="Kur (boş = güncel kur)" className="brut-input"
                  disabled={charge.currency === "TRY"} value={charge.currency === "TRY" ? "" : charge.fx_rate}
                  onChange={(e) => setCharge({ ...charge, fx_rate: e.target.value })} data-testid="charge-fxrate-input" />
                <select className="brut-input" value={charge.supplier_id} onChange={(e) => setCharge({ ...charge, supplier_id: e.target.value })} data-testid="charge-supplier-select">
                  <option value="">Tedarikçi (ops.)</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input placeholder="Fatura No" className="brut-input" value={charge.invoice_no}
                  onChange={(e) => setCharge({ ...charge, invoice_no: e.target.value })} data-testid="charge-invoiceno-input" />
                <button className="brut-btn" data-testid="charge-save-button">Kalem Ekle</button>
                <label className="flex items-center gap-2 text-xs text-zinc-600 sm:col-span-6">
                  <input type="checkbox" checked={charge.is_extra} onChange={(e) => setCharge({ ...charge, is_extra: e.target.checked })} data-testid="charge-extra-checkbox" />
                  Ek masraf (onaylanmadan gelir/maliyete yansımaz)
                </label>
              </form>
            )}
          </div>
        </div>
      )}

      {tab === "portal" && (
        <div className="brut p-4 sm:p-6" data-testid="tab-content-portal">
          <p className="overline">Müşteri Portalı Paylaşımı</p>
          <p className="mt-2 text-sm text-zinc-600">Portal varsayılan olarak kapalıdır. Yalnızca seçilen alanlar ve belgeler müşteriye görünür.</p>
          <label className="mt-4 flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={portal.enabled} onChange={(e) => setPortal({ ...portal, enabled: e.target.checked })} data-testid="portal-enabled-checkbox" />
            Bu operasyonu müşteri portalında göster
          </label>
          <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <p className="overline">Paylaşılacak Alanlar</p>
              <div className="mt-2 space-y-1.5">
                {PORTAL_FIELDS.map((f) => (
                  <label key={f.k} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={portal.fields.includes(f.k)} data-testid={`portal-field-${f.k}`}
                      onChange={(e) => setPortal({ ...portal, fields: e.target.checked ? [...portal.fields, f.k] : portal.fields.filter((x) => x !== f.k) })} />
                    {f.l}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="overline">Paylaşılacak Belgeler</p>
              <div className="mt-2 space-y-1.5">
                {data.documents.map((dc) => (
                  <label key={dc.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={portal.document_ids.includes(dc.id)} data-testid={`portal-doc-${dc.id}`}
                      onChange={(e) => setPortal({ ...portal, document_ids: e.target.checked ? [...portal.document_ids, dc.id] : portal.document_ids.filter((x) => x !== dc.id) })} />
                    {dc.name} {dc.status !== "tamam" && <span className="text-xs text-zinc-400">(yüklenmedi)</span>}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {can("operation:write") && (
            <button className="brut-btn mt-6" data-testid="portal-save-button"
              onClick={() => act(() => api.post(`/operations/${id}/portal-share`, portal), "Portal paylaşım ayarları kaydedildi")}>
              Paylaşımı Kaydet
            </button>
          )}
          <button className="brut-btn-ghost ml-3 mt-6" data-testid="portal-preview-button"
            onClick={async () => {
              try {
                const { data: p } = await api.get(`/operations/${id}/portal-preview`);
                setPreview(p);
              } catch (e) { toast.error(errMsg(e)); }
            }}>
            <Eye className="h-4 w-4" /> Müşteri Görünümü Önizle
          </button>

          {preview && (
            <div className="brut mt-6 border-2 border-klein p-4" data-testid="portal-preview">
              <div className="flex items-center justify-between">
                <p className="overline">Müşteri Portalı Önizlemesi</p>
                <button className="text-xs text-zinc-500 hover:text-zinc-950" onClick={() => setPreview(null)} data-testid="portal-preview-close">Kapat</button>
              </div>
              {!preview.portal_enabled && (
                <p className="mt-2 border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                  Portal kapalı: kaydettikten sonra müşteri bu dosyayı hiç görmez.
                </p>
              )}
              <p className="mt-3 font-mono text-lg font-bold">{preview.no}</p>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                {preview.status && <div><p className="overline">Durum</p><Badge value={preview.status} /></div>}
                {preview.route && <div><p className="overline">Rota</p><p>{preview.route}</p></div>}
                {preview.mode && <div><p className="overline">Mod</p><p>{MODE_LABELS[preview.mode]}</p></div>}
                {preview.eta && <div><p className="overline">Tahmini Varış</p><p>{d(preview.eta)}</p></div>}
                {preview.cargo_desc && <div><p className="overline">Yük</p><p>{preview.cargo_desc}</p></div>}
                {preview.weight_kg != null && preview.cargo_desc && <div><p className="overline">Ağırlık</p><p className="tnum font-mono">{preview.weight_kg} kg</p></div>}
              </div>
              {preview.events && (
                <p className="mt-3 text-xs text-zinc-600">{preview.events.length} olay kaydı müşteriye gösterilecek</p>
              )}
              {preview.documents && (
                <p className="mt-1 text-xs text-zinc-600">
                  Paylaşılan belgeler: {preview.documents.map((x) => x.name).join(", ") || "yok"}
                </p>
              )}
              {!preview.status && !preview.route && !preview.mode && !preview.eta && !preview.cargo_desc &&
                !preview.events && !preview.documents && (
                <p className="mt-3 text-sm text-zinc-500">Hiçbir alan paylaşılmıyor: müşteri yalnızca dosya numarasını görür.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ShipmentForm = ({ onSave }) => {
  const [f, setF] = useState({ description: "", weight_kg: "", packages: "", kind: "kismi" });
  return (
    <form className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4" data-testid="shipment-form"
      onSubmit={(e) => { e.preventDefault(); onSave({ ...f, weight_kg: Number(f.weight_kg || 0), packages: Number(f.packages || 0) }); setF({ description: "", weight_kg: "", packages: "", kind: "kismi" }); }}>
      <select className="brut-input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="shipment-kind-select">
        <option value="kismi">Kısmi</option><option value="birlestirme">Birleştirme</option><option value="bolme">Bölme</option>
      </select>
      <input required placeholder="Açıklama" className="brut-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="shipment-description-input" />
      <input type="number" placeholder="kg" className="brut-input" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} data-testid="shipment-weight-input" />
      <button className="brut-btn" data-testid="shipment-save-button">Ekle</button>
    </form>
  );
};
